import { useEffect, useRef, useState } from "react";
import { Link, Navigate, Outlet, Route, Routes, useNavigate, useSearchParams } from "react-router-dom";
import type { IssuedMcpToken, McpTokenSummary, OAuthConnection } from "@/types";
import { Button } from "@/ui";
import { AppShell } from "@/features/shell/AppShell";
import { SectionHead } from "@/features/shell/EmptyState";
import { ActivitiesPage } from "@/features/activities/ActivitiesPage";
import { PlanningPage } from "@/features/planning/PlanningPage";
import { TimelinePage } from "@/features/timeline/TimelinePage";
import { MoneyPage } from "@/features/money/MoneyPage";
import { PeoplePage } from "@/features/people/PeoplePage";
import { shortDate } from "@/lib/date";
import { client } from "./services/client";
import { appleSignIn } from "./services/appleSignIn";
import { tokenStore } from "./auth/tokenStore";
import { onSessionExpired } from "./auth/session";

const BRAND_MARK = "A8";
const TOKEN_PARAM = "token";
const MCP_ENDPOINT = "https://mcp.assistant8.com/mcp";
/// Where to send the browser after a magic-link sign-in that began mid-flow (e.g.
/// an OAuth authorize request), so the round-trip returns to where it started.
const POST_AUTH_REDIRECT_KEY = "a8.postAuthRedirect";

/** Email magic-link sign-in plus Sign in with Apple. */
function SignIn({ onAuthenticated }: { onAuthenticated: () => void }) {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Preload the Apple SDK + pre-hash a nonce so the popup opens synchronously on
  // click. Safari blocks window.open that isn't opened inside the user gesture,
  // so any await before signIn() (SDK load, nonce hashing) must happen up front.
  useEffect(() => {
    void appleSignIn.prepare();
  }, []);

  if (tokenStore.get()) return <Navigate to="/" replace />;

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    try {
      await client.requestMagicLink(email);
      setSent(true);
    } catch {
      setError("Could not send the link. Please try again.");
    }
  }

  // Apple provisions on first use and returns the same identity afterwards, so
  // this single flow serves both sign up and sign in.
  async function withApple() {
    setError(null);
    try {
      const { identityToken, nonce } = await appleSignIn.identityToken();
      tokenStore.setPair(await client.signInWithApple(identityToken, nonce));
      onAuthenticated();
    } catch {
      setError("Apple sign-in failed. Please try again.");
    }
  }

  return (
    <main className="auth">
      <section className="a8-card auth-card">
        <span className="a8-badge">{BRAND_MARK}</span>
        <p className="a8-eyebrow">Private console</p>
        <h1 className="auth-title">Assistant8 Admin</h1>
        <p className="auth-lede">Sign in or create your account.</p>
        {sent ? (
          <p className="auth-lede">Check your email for a sign-in link.</p>
        ) : (
          <form className="auth-form" onSubmit={submit}>
            <input
              type="email"
              required
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="a8-input"
            />
            <Button type="submit">Email me a magic link</Button>
          </form>
        )}
        {/* Apple stays reachable regardless of the magic-link state. */}
        {appleSignIn.isConfigured && (
          <div className="auth-form">
            <span className="auth-divider">or</span>
            <Button type="button" variant="secondary" onClick={withApple}>
              Continue with Apple
            </Button>
          </div>
        )}
        {error && <p className="auth-error">{error}</p>}
        <p className="auth-footnote">Access is restricted to administrators.</p>
      </section>
    </main>
  );
}

/** Magic-link landing: reads ?token, exchanges it for tokens, then redirects. */
function Callback() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  // Magic-link tokens are single-use. React 18 StrictMode double-invokes effects
  // in dev, which would spend the token on the first call and fail the second —
  // rendering a spurious "invalid or expired". Guard so verify runs exactly once.
  const verified = useRef(false);

  useEffect(() => {
    if (verified.current) return;
    const token = params.get(TOKEN_PARAM);
    if (!token) {
      setError("Missing sign-in token.");
      return;
    }
    verified.current = true;
    client
      .verifyMagicLink(token)
      .then((pair) => {
        tokenStore.setPair(pair);
        // Resume an interrupted flow (e.g. OAuth consent) if one was stashed.
        const next = sessionStorage.getItem(POST_AUTH_REDIRECT_KEY);
        sessionStorage.removeItem(POST_AUTH_REDIRECT_KEY);
        navigate(next ?? "/", { replace: true });
      })
      .catch(() => setError("This link is invalid or has expired."));
  }, [params, navigate]);

  return (
    <main className="auth">
      <section className="a8-card auth-card">
        <span className="a8-badge">{BRAND_MARK}</span>
        <p className="auth-lede">{error ?? "Signing you in…"}</p>
        {error && <a href="/">Back to sign in</a>}
      </section>
    </main>
  );
}

/** The one-time reveal of a freshly minted token, with a copy button. */
function NewTokenReveal({ issued }: { issued: IssuedMcpToken }) {
  const [copied, setCopied] = useState(false);

  async function copy() {
    await navigator.clipboard.writeText(issued.token);
    setCopied(true);
  }

  return (
    <div className="a8-card token-reveal">
      <p className="a8-eyebrow">New token — copy it now</p>
      <p className="token-warning">
        This is the only time the full token is shown. Store it somewhere safe;
        you won't be able to see it again.
      </p>
      <code className="token-secret">{issued.token}</code>
      <Button type="button" onClick={copy}>
        {copied ? "Copied" : "Copy token"}
      </Button>
    </div>
  );
}

/**
 * Account settings: manage MCP personal access tokens (generate, list, revoke)
 * and connected OAuth apps (list, revoke). Tokens are durable credentials pasted
 * into an MCP client; connections are apps authorized via the OAuth flow.
 * Auth-gated like the console.
 */
function Settings() {
  const [tokens, setTokens] = useState<McpTokenSummary[]>([]);
  const [connections, setConnections] = useState<OAuthConnection[]>([]);
  const [label, setLabel] = useState("");
  const [issued, setIssued] = useState<IssuedMcpToken | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    client.listMcpTokens().then(setTokens).catch(() => setError("Could not load tokens."));
    client
      .listOAuthConnections()
      .then(setConnections)
      .catch(() => setError("Could not load connected apps."));
  }, []);

  async function generate(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const token = await client.createMcpToken(label.trim());
      setIssued(token);
      setLabel("");
      setTokens(await client.listMcpTokens());
    } catch {
      setError("Could not generate the token.");
    } finally {
      setBusy(false);
    }
  }

  async function revoke(id: string) {
    setError(null);
    try {
      await client.deleteMcpToken(id);
      setTokens((current) => current.filter((t) => t.id !== id));
      if (issued?.id === id) setIssued(null);
    } catch {
      setError("Could not revoke the token.");
    }
  }

  async function disconnect(id: string) {
    setError(null);
    try {
      await client.revokeOAuthConnection(id);
      setConnections((current) => current.filter((c) => c.id !== id));
    } catch {
      setError("Could not disconnect the app.");
    }
  }

  return (
    <main className="console console-narrow">
      <SectionHead title="Settings" />
      <section className="a8-card">
        <h2>MCP access tokens</h2>
        <p className="auth-lede">
          Generate a token to connect an MCP client (e.g. Claude Code) to{" "}
          <code>{MCP_ENDPOINT}</code> with an{" "}
          <code>Authorization: Bearer &lt;token&gt;</code> header.
        </p>
        <form className="auth-form" onSubmit={generate}>
          <input
            type="text"
            required
            maxLength={100}
            placeholder="Label (e.g. Claude Code)"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            className="a8-input"
          />
          <Button type="submit" disabled={busy}>
            {busy ? "Generating…" : "Generate token"}
          </Button>
        </form>
        {error && <p className="auth-error">{error}</p>}
      </section>

      {issued && <NewTokenReveal issued={issued} />}

      {tokens.length === 0 ? (
        <p className="console-empty">No tokens yet.</p>
      ) : (
        <ul className="goal-list">
          {tokens.map((t) => (
            <li key={t.id} className="a8-card token-row">
              <span className="token-id">
                <code>{t.prefix}…</code> {t.label}
              </span>
              <span className="token-expiry">Expires {shortDate(t.expiresAt)}</span>
              <Button type="button" variant="secondary" onClick={() => revoke(t.id)}>
                Revoke
              </Button>
            </li>
          ))}
        </ul>
      )}

      <section className="a8-card">
        <h2>Connected apps</h2>
        <p className="auth-lede">
          MCP clients (e.g. ChatGPT, Claude) you've authorized to access your activities
          via OAuth. Revoke to sign an app out.
        </p>
      </section>

      {connections.length === 0 ? (
        <p className="console-empty">No connected apps.</p>
      ) : (
        <ul className="goal-list">
          {connections.map((c) => (
            <li key={c.id} className="a8-card token-row">
              <span className="token-id">{c.clientName ?? "Unnamed app"}</span>
              <span className="token-expiry">Connected {shortDate(c.createdAt)}</span>
              <Button type="button" variant="secondary" onClick={() => disconnect(c.id)}>
                Disconnect
              </Button>
            </li>
          ))}
        </ul>
      )}
    </main>
  );
}

/**
 * OAuth consent screen. The authorization server redirects the browser here with
 * the authorize params; after login the user approves, we mint a code via the
 * backend, and navigate to the client's redirect URL. Reuses the app sign-in.
 */
function Authorize() {
  const [params] = useSearchParams();
  const [authed, setAuthed] = useState(() => tokenStore.get() !== null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const clientId = params.get("client_id");
  const redirectUri = params.get("redirect_uri");
  const resource = params.get("resource");
  const codeChallenge = params.get("code_challenge");
  const codeChallengeMethod = params.get("code_challenge_method");
  const state = params.get("state") ?? undefined;

  const complete = clientId && redirectUri && resource && codeChallenge && codeChallengeMethod;
  if (!complete) {
    return (
      <main className="auth">
        <section className="a8-card auth-card">
          <span className="a8-badge">{BRAND_MARK}</span>
          <p className="auth-error">This authorization request is invalid or incomplete.</p>
        </section>
      </main>
    );
  }

  // Not signed in: stash this URL so a magic-link round-trip returns here.
  if (!authed) {
    sessionStorage.setItem(POST_AUTH_REDIRECT_KEY, `${window.location.pathname}${window.location.search}`);
    return <SignIn onAuthenticated={() => setAuthed(true)} />;
  }

  async function approve() {
    setError(null);
    setBusy(true);
    try {
      const { redirectTo } = await client.consentToAuthorize({
        clientId: clientId!,
        redirectUri: redirectUri!,
        codeChallenge: codeChallenge!,
        codeChallengeMethod: codeChallengeMethod!,
        resource: resource!,
        state,
      });
      window.location.href = redirectTo;
    } catch {
      setError("Could not complete authorization. Please try again.");
      setBusy(false);
    }
  }

  async function deny() {
    setError(null);
    setBusy(true);
    try {
      // Route through the backend so it re-validates redirect_uri against the
      // registered client — never trust the raw query param (audit M6).
      const { redirectTo } = await client.denyAuthorize({
        clientId: clientId!,
        redirectUri: redirectUri!,
        state,
      });
      window.location.href = redirectTo;
    } catch {
      setError("Could not complete the request. Please try again.");
      setBusy(false);
    }
  }

  return (
    <main className="auth">
      <section className="a8-card auth-card">
        <span className="a8-badge">{BRAND_MARK}</span>
        <p className="a8-eyebrow">Authorize access</p>
        <h1 className="auth-title">Connect to Assistant8</h1>
        <p className="auth-lede">
          An MCP client wants to access your Assistant8 activities on your behalf. Only
          approve if you started this connection.
        </p>
        <p className="auth-footnote">
          Resource: <code>{resource}</code>
        </p>
        <div className="auth-form">
          <Button type="button" onClick={approve} disabled={busy}>
            {busy ? "Authorizing…" : "Approve"}
          </Button>
          <Button type="button" variant="secondary" onClick={deny} disabled={busy}>
            Deny
          </Button>
        </div>
        {error && <p className="auth-error">{error}</p>}
      </section>
    </main>
  );
}

/** Sign in as a top-level route; on success land on the console. */
function SignInRoute() {
  const navigate = useNavigate();
  if (tokenStore.get()) return <Navigate to="/" replace />;
  return <SignIn onAuthenticated={() => navigate("/", { replace: true })} />;
}

/**
 * Layout route guarding the authenticated area. Redirects to /signin when there is
 * no session and subscribes ONCE, app-wide, to session expiry — so any deep refresh
 * failure drops the whole app back to sign-in, not just the page that happened to
 * mount the listener (audit W6).
 */
function RequireAuth() {
  const [authed, setAuthed] = useState(() => tokenStore.get() !== null);
  useEffect(() => onSessionExpired(() => setAuthed(false)), []);
  if (!authed) return <Navigate to="/signin" replace />;
  return <Outlet />;
}

export function App() {
  return (
    <Routes>
      <Route path="/signin" element={<SignInRoute />} />
      <Route path="/auth/callback" element={<Callback />} />
      <Route path="/authorize" element={<Authorize />} />
      {/* Everything below the guard shares one shell: one app bar, one set of root
       * tabs, and one sync feed started once for every page. */}
      <Route element={<RequireAuth />}>
        <Route element={<AppShell />}>
          <Route path="/" element={<ActivitiesPage />} />
          <Route path="/timeline" element={<TimelinePage />} />
          <Route path="/planning" element={<PlanningPage />} />
          <Route path="/money" element={<MoneyPage />} />
          <Route path="/people" element={<PeoplePage />} />
          <Route path="/settings" element={<Settings />} />
        </Route>
      </Route>
    </Routes>
  );
}
